import React, { useEffect } from 'react';

import moment from 'moment';
import secureAxios, { isAxiosError } from '../core/api/client/secureAxios';

import {
    AD_ENTITY_TYPES,
    AUTH_TYPES_TO_SHOW,
    DEFAULT_MASK,
    DIRECTORY_TYPES,
    ENABLED_PROTOCOLS_TO_PROTECT,
    HTTP_OPTIONS,
    INCIDENT_STATUS,
    INVESTIGATION_ENTITY_TYPES,
    IP_AND_MASK_PATTERN,
    IP_PATTERN,
    LOGS_PAGE_URL_PREFIX,
    MFA_TYPES,
    NODES_STATES,
    POLICY_ACTION_TYPES,
    PREDICTABILITY_LEVELS,
    PROTOCOL_DISPLAY_NAMES,
    PROTOCOLS_GRAPHS_COLORS
} from '@silverfort-nodelibs/common/utils/consts';
import { Tooltip } from '@material-ui/core';
import { get, isEmpty } from 'lodash';
import FileSaver from 'file-saver';
import json2csv from 'json2csv';
import { getRiskIndicatorByRiskRule } from '@silverfort-nodelibs/common/utils/insight';

async function httpReq(method, url, reqData, options = {}) {
    const res = { data: null, status: null, error: null };
    const config = {};

    try {
        config.method = method;
        config.url = url;
        if (reqData) config.data = reqData;
        if (options[HTTP_OPTIONS.REQ_OPTIONS]) Object.assign(config, options[HTTP_OPTIONS.REQ_OPTIONS]);

        const httpResponse = await secureAxios.request(config);

        res.data = httpResponse.data;
        res.status = httpResponse.status ?? null;

        const redirectUrl = httpResponse?.data?.redirect;

        if (redirectUrl) {
            handleRedirect(redirectUrl, options);
        }
    } catch (err) {
        if (isAxiosError(err)) {
            const redirectUrl = err.response?.data?.redirect;

            if (redirectUrl) {
                handleRedirect(redirectUrl, options);
            }

            console.error(err.response?.data, err.toJSON());
        } else {
            console.error(err);
        }

        if (options[HTTP_OPTIONS.HANDLE_ERRORS]) {
            res.status = err.response?.status;
            res.error = err;
        } else throw err;
    }

    return res;
}

function handleRedirect(redirectUrl, options) {
    if (options[HTTP_OPTIONS.REDIRECT_TO_NEW_TAB]) {
        const redirectWindow = window.open(redirectUrl, '_blank');
        redirectWindow?.focus();
    } else {
        window.location.href = redirectUrl;
    }
}

async function httpGet(url, options) {
    return httpReq('get', url, null, options);
}

async function httpPost(url, reqData, options) {
    return httpReq('post', url, reqData, options);
}

async function httpPut(url, reqData, options) {
    return httpReq('put', url, reqData, options);
}

async function httpDelete(url, reqData, options) {
    return httpReq('delete', url, reqData, options);
}

async function httpPatch(url, reqData, options) {
    return httpReq('patch', url, reqData, options);
}

async function httpHead(url, reqData, options) {
    return httpReq('head', url, reqData, options);
}

async function updateUserToState(self, cb = null) {
    const response = await httpGet('/data/get-current-user', { [HTTP_OPTIONS.HANDLE_ERRORS]: true });
    if (response.data) {
        secureAxios.defaults.headers.common['csrf-token'] = response.data.csrf;
        self?.setState(
            { user: response.data.user, sessionTimeout: response.data.sessionTimeout, csrf: response.data.csrf },
            cb
        );
    }
}

export function dateFormat(date) {
    return moment(date).format('MMM D, YYYY HH:mm');
}

async function getUserState() {
    const response = await httpGet('/data/get-current-user', { [HTTP_OPTIONS.HANDLE_ERRORS]: true });
    if (response.data) {
        secureAxios.defaults.headers.common['csrf-token'] = response.data.csrf;
        return response.data;
    }
}

function bigNumberToText(bigNumber, spacedLabel = true, fractionDigits = 1) {
    if (!bigNumber || isNaN(bigNumber)) {
        return 0;
    }
    return bigNumber > 999999999
        ? (bigNumber / 1000000000).toFixed(fractionDigits) + `${spacedLabel ? ' ' : ''}B`
        : bigNumber > 999999
        ? (bigNumber / 1000000).toFixed(fractionDigits) + `${spacedLabel ? ' ' : ''}M`
        : bigNumber > 999
        ? (bigNumber / 1000).toFixed(fractionDigits) + `${spacedLabel ? ' ' : ''}K`
        : bigNumber;
}

function namesToMinimalistList(items, multiEntitiesHeader = 'groups', nameField = 'name') {
    if (items && items.length > 0) {
        if (items.length === 1) {
            return (
                <div>
                    <span>{items[0][nameField]}</span>
                </div>
            );
        } else {
            return (
                <div>
                    <span>
                        {items.length} {multiEntitiesHeader}
                    </span>
                </div>
            );
        }
    } else {
        return false;
    }
}

const entitiesToSpans = (items) => {
    if (items && items.length > 0) {
        let innerElem;
        if (items.length === 1) {
            innerElem = entityToSpan(items[0]);
        } else if (items.length === 2) {
            innerElem = (
                <>
                    {entityToSpan(items[0])} {entityToSpan(items[1])}
                </>
            );
        } else {
            innerElem = (
                <>
                    {entityToSpan(items[0])} {entityToSpan(items[1])} and{' '}
                    <Tooltip
                        title={
                            <div className="namesToShortListMoreDiv">
                                {items
                                    .slice(2)
                                    .map((item) => {
                                        return item.displayText;
                                    })
                                    .join('\n')}
                            </div>
                        }
                        enterDelay={100}
                        placement={'right'}
                        interactive={true}
                        arrow
                        classes={{ tooltip: 'settings-tooltip' }}>
                        <span>{items.length - 2} more</span>
                    </Tooltip>
                </>
            );
        }
        return <div className="namesToShortListDiv">{innerElem}</div>;
    } else {
        return '';
    }
};

const entityToSpan = (item) => {
    return (
        <span>
            {item.title ? (
                <span className="itemDisplaySpan" title={item.title}>
                    {item.displayText}
                </span>
            ) : (
                <span>{item.displayText}</span>
            )}
        </span>
    );
};

function unixDateToText(date, emptyValue = 'n/a') {
    const locale = navigator.languages ? navigator.languages : navigator.language;
    moment.locale(locale);
    if (!date) {
        return emptyValue;
    } else {
        return `${moment.unix(date).local().format('L')}, ${moment.unix(date).local().format('LTS')}`;
    }
}

function numberToHour(value) {
    if (value === 0) {
        return `12am`;
    } else if (value < 12) {
        return `${value}am`;
    } else if (value === 12) {
        return `12pm`;
    } else if (value < 24) {
        return `${value - 12}pm`;
    }

    return '';
}

function getFilteredLogsUrl(filtersObj) {
    if (!filtersObj) {
        return LOGS_PAGE_URL_PREFIX;
    }
    let url = `${LOGS_PAGE_URL_PREFIX}?`;
    for (const filterType in filtersObj) {
        const filterValue = Array.isArray(filtersObj[filterType])
            ? filtersObj[filterType].join(',')
            : filtersObj[filterType];
        url = `${url}${filterType}=${filterValue}&`;
    }
    return url.slice(0, -1);
}

function getInvestigateUrl(identifier, type, displayNameHint) {
    let result;
    if (type === AD_ENTITY_TYPES.USER) {
        result = INVESTIGATION_ENTITY_TYPES.USER.urlPrefix + identifier;
    } else if (type === AD_ENTITY_TYPES.APPLICATION) {
        result = INVESTIGATION_ENTITY_TYPES.APPLICATION.urlPrefix + identifier;
    } else if (type === AD_ENTITY_TYPES.OKTA) {
        result = INVESTIGATION_ENTITY_TYPES.OKTA.urlPrefix + identifier;
    } else {
        result = INVESTIGATION_ENTITY_TYPES.RESOURCE.urlPrefix + identifier;
    }

    if (displayNameHint) {
        result = `${result}?displayName=${displayNameHint}`;
    }
    return result;
}

function getEntityType(entityType, entityData) {
    if (entityType === AD_ENTITY_TYPES.SERVER) {
        if (entityData.authType === DIRECTORY_TYPES.aad.key) {
            return AD_ENTITY_TYPES.APPLICATION;
        } else if (entityData.authType === DIRECTORY_TYPES.okta.key) {
            return AD_ENTITY_TYPES.OKTA;
        }
    }
    return entityType;
}

function nodeStateByID(stateID) {
    let result = {};
    Object.values(NODES_STATES).some((stateData) => {
        if (stateID === stateData.ID) {
            result = stateData;
            return true;
        }
        return false;
    });
    return result;
}

function removeTrailingTags(version) {
    let newVersion = version;

    if (newVersion) {
        const parts = newVersion.toString().split('-');
        const lastPart = parts[parts.length - 1].toLowerCase();
        if (lastPart === 'rc' || lastPart === 'release') {
            newVersion = parts.slice(0, parts.length - 1).join('-');
        }
    }

    return newVersion;
}

function isUserPermitted(user, permissionName, permissionLevel) {
    return (
        user !== undefined && user !== null && user.permissions && user.permissions[permissionName] >= permissionLevel
    );
}

function uppercaseFirstLetter(lowercaseString) {
    if (!lowercaseString || lowercaseString.length === 0) {
        return lowercaseString;
    }
    if (lowercaseString.length === 1) {
        return lowercaseString.toUpperCase();
    }
    return lowercaseString.charAt(0).toUpperCase() + lowercaseString.slice(1);
}

function isEmailValid(emailAddr) {
    const regex =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regex.test(String(emailAddr).toLowerCase());
}

function isPasswordValid(password) {
    // one or more digits, one or more special characters, one or more letters (lower or upper case), 8 or more chars long
    const regex = /^(?=.*\d)(?=.*[$&+,:;=?@#|'<>.^*()%!-])(?=.*[A-z]).{8,}$/;
    return regex.test(password);
}

function predictabilityLevelToText(predictability) {
    let result = 'None';
    for (const predictabilityLevel of Object.values(PREDICTABILITY_LEVELS)) {
        if (predictabilityLevel.id === predictability) {
            result = predictabilityLevel.display;
            break;
        }
    }
    return result;
}

function getAuthsByProtocolData(usagesData, totalAuths, threshold = 0) {
    return getAuthsByData(
        Object.keys(PROTOCOL_DISPLAY_NAMES),
        PROTOCOL_DISPLAY_NAMES,
        usagesData,
        totalAuths,
        threshold
    );
}

function getAuthsByAuthTypeData(usagesData, totalAuths, threshold = 0) {
    const values = {};
    for (const authType of Object.keys(AUTH_TYPES_TO_SHOW)) {
        values[authType] = AUTH_TYPES_TO_SHOW[authType].label;
    }
    return getAuthsByData(Object.keys(AUTH_TYPES_TO_SHOW), values, usagesData, totalAuths, threshold);
}

function getAuthsByData(keys, values, usagesData, totalAuths, threshold = 0) {
    const authsByProtocolData = [],
        authsByProtocolDataColors = [];

    let lowCountAuths = 0;
    for (const protocolName of keys) {
        if (usagesData[protocolName] > 0) {
            if (parseInt((usagesData[protocolName] / totalAuths) * 100).toFixed(0) <= threshold) {
                lowCountAuths += usagesData[protocolName];
            } else {
                authsByProtocolData.push({
                    name: ((usagesData[protocolName] / totalAuths) * 100).toFixed(0) + '%',
                    authentications: usagesData[protocolName],
                    protocol: values[protocolName]
                });
                authsByProtocolDataColors.push(PROTOCOLS_GRAPHS_COLORS[protocolName]);
            }
        } else {
            usagesData[protocolName] = 0;
        }
    }

    if (lowCountAuths > 0) {
        authsByProtocolData.push({
            name: ((lowCountAuths / totalAuths) * 100).toFixed(0) + '%',
            authentications: lowCountAuths,
            protocol: 'Other protocols'
        });
        authsByProtocolDataColors.push(PROTOCOLS_GRAPHS_COLORS['OTHER']);
    }

    return { authsByProtocolData, authsByProtocolDataColors };
}

function getTokenTypeNameFromId(typeId, deviceName) {
    if (typeId === MFA_TYPES.MOBILE.ID) {
        return `${MFA_TYPES.MOBILE.DISPLAY_NAME} (${deviceName})`;
    }
    if (typeId === MFA_TYPES.CHROME.ID) {
        return MFA_TYPES.CHROME.DISPLAY_NAME;
    }
    if (typeId === MFA_TYPES.DESKTOP_APP.ID) {
        return MFA_TYPES.DESKTOP_APP.DISPLAY_NAME;
    }
    if (typeId === MFA_TYPES.HARDWARE.ID) {
        return MFA_TYPES.HARDWARE.DISPLAY_NAME;
    }
    if (typeId === MFA_TYPES.OTP.ID) {
        return MFA_TYPES.OTP.DISPLAY_NAME;
    }
}

async function sendMFATokenTest(
    selectedUser,
    testFactor = null,
    tokenId = null,
    tokenType = null,
    device = null,
    baseUrl = 'Settings'
) {
    try {
        const requestData = {
            username: selectedUser.sam || selectedUser.user_name,
            domainName: selectedUser.domain_name
        };

        if (testFactor) {
            requestData.authenticator = testFactor;
        }
        if (tokenId) {
            requestData.provider_ids = Array.isArray(tokenId) ? tokenId : [tokenId];
            requestData.tokenId = tokenId;
            requestData.tokenType = tokenType;
            requestData.device = device;
        }

        const response = await httpPost(`/${baseUrl}/testMFAToken`, requestData, {
            [HTTP_OPTIONS.HANDLE_ERRORS]: true
        });
        if (response.status !== 200) {
            const text = `Failed to send test.${
                tokenType === MFA_TYPES.DESKTOP_APP.ID
                    ? ' Please verify the connection to Desktop Messaging Services.'
                    : ''
            }`;
            return { text, error: true };
        } else {
            const responseData = response.data;
            return {
                text: responseData.responseText,
                error: !responseData.sentSuccessfully,
                wallID: responseData.wallID,
                requestID: responseData.requestID
            };
        }
    } catch (err) {
        return { text: `Test failed.`, error: true };
    }
}

function getAuthTypeProtocols(authType) {
    return AUTH_TYPES_TO_SHOW[authType]?.protocols || [];
}

function formatEventDescription(description, dc) {
    if (dc) {
        const dcIndex = description.toLowerCase().indexOf(`${dc.toLowerCase()}: `);
        if (dcIndex !== -1) {
            return `${dc}: ${uppercaseFirstLetter(description.toLowerCase().replace(`${dc.toLowerCase()}: `, ''))}`;
        } else {
            return `${dc}: ${uppercaseFirstLetter(description.toLowerCase())}`;
        }
    }
    return uppercaseFirstLetter(description);
}

function downloadFile(url, overrideFileName = '') {
    const link = document.createElement('a');
    link.style.display = 'none';
    link.href = url; // For Local env use: 'http://localhost:4000' + url
    link.setAttribute('download', overrideFileName || 'true');

    if (typeof link.download === 'undefined') {
        link.setAttribute('target', '_blank');
    }

    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
        document.body.removeChild(link);
    }, 200);
}

function localTimezoneUtc() {
    const hoursOffset = Math.floor(moment().utcOffset() / 60);
    const minutesOffset = moment().utcOffset() % 60;
    let formattedUtcZone = hoursOffset.toString();
    if (minutesOffset !== 0) {
        formattedUtcZone = `${formattedUtcZone}:${minutesOffset}`;
    }

    return `(UTC ${hoursOffset >= 0 ? '+' : ''}${formattedUtcZone})`;
}

function useInterval(callback, intervalTime) {
    useEffect(() => {
        const timerId = setInterval(() => {
            callback();
        }, intervalTime);

        return () => {
            clearInterval(timerId);
        };
    }, [callback, intervalTime]);
}

function getDefaultIndicatorsSelectionsBySubType(
    subTypeRiskRulesList,
    defaultIsChecked,
    negateIsChecked,
    cacheKey,
    groupName
) {
    let cachedOptions = null;

    if (cacheKey) {
        const cachedStrOptions = localStorage.getItem(cacheKey);
        if (cachedStrOptions) {
            const parsedOptions = JSON.parse(cachedStrOptions) || {};
            cachedOptions = parsedOptions[groupName || cacheKey];
        }
    }

    const indicatorsSelection = {};
    for (const key of subTypeRiskRulesList) {
        let isChecked = defaultIsChecked;
        if (cachedOptions) {
            isChecked = cachedOptions.includes(key);
        } else if (negateIsChecked.includes(key)) {
            isChecked = !defaultIsChecked;
        }
        indicatorsSelection[key] = { title: getRiskIndicatorByRiskRule(key).insightsTitle, isChecked: isChecked };
    }
    return indicatorsSelection;
}

const sortObjectsByProperties = (objectArray, sortBy, reverse) => {
    let sortByArray = sortBy;
    if (!Array.isArray(sortByArray)) {
        sortByArray = [sortByArray];
    }
    return [...objectArray].sort((a, b) => {
        const aSortBy = sortByArray.find((field) => a[field]);
        const bSortBy = sortByArray.find((field) => b[field]);

        if (!aSortBy) {
            return reverse ? -1 : 1;
        }
        if (!bSortBy) {
            return reverse ? 1 : -1;
        }
        if (a[aSortBy] > b[bSortBy]) {
            return reverse ? 1 : -1;
        } else if (a[aSortBy] < b[bSortBy]) {
            return reverse ? -1 : 1;
        }
        return 0;
    });
};

const isIpSubnetValid = (ip, mask) => {
    return mask ? IP_AND_MASK_PATTERN.test(`${ip}/${mask}`) : IP_PATTERN.test(ip);
};

const addIpsFromFile = (file, addIp, setFileError, setFileLoading, maxFileSize, maxFileSizeText, exclude = false) => {
    setFileLoading(true);
    const maxFileSizeCheck = maxFileSize ? maxFileSize : 1000000;
    const maxFileSizeTextCheck = maxFileSizeText ? maxFileSizeText : '1 MB';
    if (file.size > maxFileSizeCheck) {
        setFileLoading(false);
        return setFileError(`File exceeds maximum size of ${maxFileSizeTextCheck}`);
    }

    const fileReader = new FileReader();
    setFileError('');
    setFileLoading(false);
    fileReader.onload = (e) => {
        const fileContent = e.target.result;
        const subnets = fileContent.split(',');
        for (const subnet of subnets) {
            const ipAdderss = subnet.split('/')[0];
            const mask = subnet.split('/')[1] || '';
            const newIp = {
                name: ipAdderss,
                address: ipAdderss,
                mask: mask || DEFAULT_MASK,
                excluded: exclude,
                type: AD_ENTITY_TYPES.IP
            };
            if (isIpSubnetValid(newIp.address, newIp.mask)) {
                addIp(newIp);
            }
        }
    };
    fileReader.readAsText(file);
};

const getIncidentStatusLabelByValue = (value) => {
    return Object.values(INCIDENT_STATUS).find((status) => status.value === value)?.label;
};

const getStatusOptionsByCurrentValue = (value) => {
    const incidentStatuses = Object.assign({}, INCIDENT_STATUS);
    if (value !== INCIDENT_STATUS.ONGOING.value) {
        delete incidentStatuses['ONGOING'];
    }
    return Object.values(incidentStatuses);
};

/**
 * Exports a given array of items to a CSV file.
 * If the array is empty, it will generate an empty file with headers only.
 *
 * @param {Array} rowsValues The array to be exported
 * @param {{ title: string; path: string; transformer?: () => any; }[]} rowFields Fields configuration. Path can be nested.
 * @param {string} fileName File name, without '.csv', it will be automatically added. Default is 'export'.
 * @param {boolean} appendDate Whether current date timestamp should be appended to file name. Default is true.
 */
const exportCsv = (rowsValues, rowFields, fileName = 'export', appendDate = true) => {
    let csvData = rowsValues?.map((row) => {
        const returnValue = {};

        rowFields.forEach((field) => {
            let value = get(row, field.path, null);
            if (field.transformer) {
                value = field.transformer(value);
            }
            if (Array.isArray(value)) {
                value = value.join(', ');
            }

            if (value === null || value === undefined) {
                value = '';
            }

            returnValue[field.title] = value;
        });
        return returnValue;
    });

    // If there's no data to export, generate an empty object with just titles.
    if (isEmpty(csvData)) {
        csvData = {};
        rowFields.forEach((field) => {
            csvData[field.title] = '';
        });
    }

    const csv = json2csv.parse(csvData);
    const blob = new Blob([csv], { type: 'text/plain;charset=utf-8' });
    FileSaver.saveAs(blob, `${fileName}${appendDate ? `-${moment().local().format()}` : ''}.csv`);
};

const getActionsForAuthType = (authType) => {
    const isDesktopAuth = authType === ENABLED_PROTOCOLS_TO_PROTECT.desktop_enabled.value;
    const actionTypes = Object.values(POLICY_ACTION_TYPES).filter(
        (actionType) => actionType.value !== POLICY_ACTION_TYPES.NEW.value
    );

    return isDesktopAuth
        ? actionTypes.filter((actionType) => actionType.value !== POLICY_ACTION_TYPES.BRIDGE.value)
        : actionTypes;
};

export {
    updateUserToState,
    getUserState,
    bigNumberToText,
    entitiesToSpans,
    namesToMinimalistList,
    unixDateToText,
    numberToHour,
    getFilteredLogsUrl,
    getInvestigateUrl,
    getEntityType,
    nodeStateByID,
    removeTrailingTags,
    isUserPermitted,
    uppercaseFirstLetter,
    isEmailValid,
    predictabilityLevelToText,
    getAuthsByProtocolData,
    getTokenTypeNameFromId,
    sendMFATokenTest,
    getAuthsByAuthTypeData,
    getAuthTypeProtocols,
    formatEventDescription,
    downloadFile,
    localTimezoneUtc,
    useInterval,
    getDefaultIndicatorsSelectionsBySubType,
    sortObjectsByProperties,
    isIpSubnetValid,
    addIpsFromFile,
    httpGet,
    httpPost,
    httpPut,
    httpDelete,
    httpPatch,
    httpHead,
    getIncidentStatusLabelByValue,
    getStatusOptionsByCurrentValue,
    exportCsv,
    isPasswordValid,
    getActionsForAuthType
};
